import BaseListPresenter from "../../base/BaseListPresenter";

class NotificationListPagePresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    updateObjectUseCase,
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.updateObjectUseCase = updateObjectUseCase;
  }

  init() {
    const user = this.view.getCurrentUser();
    const clientId = user?.client?.id;

    this.where = {
      ...(clientId ? { refId: clientId } : { refId: { $exists: false } }),
    };
    this.search = {};
    this.filter = {};
    this.include = ["client"];
    this.keys = [
      "title",
      "description",
      "created",
      "isRead",
      "icon",
      "route",
      "client.name",
    ];
    this.sort = { created: -1 };
    this.limit = 1000;
    this.progress = true;
    this.reset();
  }

  reset() {
    super.reset();
    if (this.view.setSelectedIds) {
      this.view.setSelectedIds([]);
    }
  }

  setNotificationRead(notif, isRead) {
    if (!notif?.id) return;
    const objects = this.view.getObjects();
    const updated = (objects || []).map((n) => {
      if (!n || n.id !== notif.id) return n;
      return { ...n, isRead, read: isRead };
    });
    this.view.setObjects(updated);
    this.updateObjectUseCase
      .execute("notifications", { id: notif.id, isRead })
      .catch((err) => this.view.showError?.(err));
  }

  setNotificationsRead(ids, isRead) {
    if (!ids?.length) return;
    const objects = this.view.getObjects();
    const idSet = new Set(ids);
    const updated = (objects || []).map((n) => {
      if (!n || !idSet.has(n.id)) return n;
      return { ...n, isRead, read: isRead };
    });
    this.view.setObjects(updated);
    this.view.setSelectedIds([]);
    Promise.all(
      ids.map((id) =>
        this.updateObjectUseCase.execute("notifications", { id, isRead }),
      ),
    ).catch((err) => this.view.showError?.(err));
  }

  removeNotifications(ids) {
    if (!ids?.length) return;
    const objects = (this.view.getObjects() || []).filter(
      (n) => !ids.includes(n.id),
    );
    this.view.setObjects(objects);
    this.view.setSelectedIds([]);
  }

  bulkMarkRead() {
    this.setNotificationsRead(this.view.getSelectedIds(), true);
  }

  bulkMarkUnread() {
    this.setNotificationsRead(this.view.getSelectedIds(), false);
  }

  async bulkDelete() {
    const ids = this.view.getSelectedIds();
    if (!ids?.length) return;
    try {
      await this.view.showDialog?.({
        title: "Delete notifications?",
        message: `Are you sure you want to delete ${ids.length} notification${ids.length !== 1 ? "s" : ""}?`,
      });
      for (const id of ids) {
        await this.deleteObjectUseCase.execute("notifications", id);
      }
      this.removeNotifications(ids);
    } catch (err) {
      if (err) this.view.showError?.(err);
    }
  }

  toggleSelect(notif) {
    const ids = this.view.getSelectedIds() || [];
    const has = ids.includes(notif.id);
    this.view.setSelectedIds(
      has ? ids.filter((id) => id !== notif.id) : [...ids, notif.id],
    );
  }

  selectAllForList(checked, items) {
    if (!items?.length) return;
    this.view.setSelectedIds(checked ? items.map((n) => n.id) : []);
  }

  onClickNotification(notif) {
    if (!notif?.id) return;
    if (!notif.isRead) this.setNotificationRead(notif, true);
    if (notif.route) this.view.navigateTo(notif.route);
  }

  async onMenuAction(notif, action) {
    if (!notif?.id) return;
    if (action === "mark-read") this.setNotificationRead(notif, true);
    if (action === "mark-unread") this.setNotificationRead(notif, false);
    if (action === "open" && notif.route) this.view.navigateTo(notif.route);
    if (action === "delete") {
      try {
        await this.view.showDialog?.({
          title: "Delete notification?",
          message: "Are you sure?",
        });
        await this.deleteObjectUseCase.execute("notifications", notif.id);
        this.removeNotifications([notif.id]);
      } catch (err) {
        if (err) this.view.showError?.(err);
      }
    }
  }
}

export default NotificationListPagePresenter;
